import React, { useContext } from 'react'
import { useGlobals } from 'context'
import { useQuery, useMutation } from '@apollo/client'
import { GET_TASK_TEMPLATES } from '../queries'
import { CHANGE_TASK_TEMPLATE, REMOVE_TASK_TEMPLATE } from '../mutations'
import { Trans } from '@lingui/macro'

const TaskTemplateContext = React.createContext()

function TaskTemplateProvider({ children }) {
  const { createError, createSuccess } = useGlobals()
  const { loading, data, refetch } = useQuery(GET_TASK_TEMPLATES)
  const { taskTemplates } = data || {}

  const newUpdate = (cache, { data: { changeTaskTemplate } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_TASK_TEMPLATES,
      })
      cache.writeQuery({
        query: GET_TASK_TEMPLATES,
        data: { taskTemplates: [...cached.taskTemplates, changeTaskTemplate] },
      })
    } catch (error) {
      // Event was not in cache
    }
  }

  const updateExisting = (cache, { data: { changeTaskTemplate } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_TASK_TEMPLATES,
      })
      cache.writeQuery({
        query: GET_TASK_TEMPLATES,
        data: {
          taskTemplates: cached.taskTemplates.map((taskTemplate) => {
            if (
              taskTemplate.task_template_id ===
              changeTaskTemplate.task_template_id
            ) {
              return {
                ...taskTemplate,
                ...changeTaskTemplate,
              }
            }
            return taskTemplate
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }

  const removeExisting = (cache, { data: { removeTaskTemplate } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_TASK_TEMPLATES,
      })
      cache.writeQuery({
        query: GET_TASK_TEMPLATES,
        data: {
          taskTemplates: cached.taskTemplates.filter((taskTemplate) => {
            return (
              taskTemplate.task_template_id !==
              removeTaskTemplate.task_template_id
            )
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }

  const [newTaskTemplate] = useMutation(CHANGE_TASK_TEMPLATE, {
    update: newUpdate,
  })

  const [updateTaskTemplate] = useMutation(CHANGE_TASK_TEMPLATE, {
    update: updateExisting,
  })

  const [removeTaskTemplate] = useMutation(REMOVE_TASK_TEMPLATE, {
    update: removeExisting,
  })

  async function create(input) {
    try {
      const response = await newTaskTemplate({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Task template was added</Trans>)
      return response.data?.changeTaskTemplate
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to add task template</Trans>,
      )
    }
  }

  async function update(input) {
    try {
      const response = await updateTaskTemplate({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Task template was updated</Trans>)
      return response.data?.changeTaskTemplate
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to add update template</Trans>,
      )
    }
  }

  async function remove(task_template_id) {
    try {
      const response = await removeTaskTemplate({
        variables: {
          task_template_id,
        },
      })
      createSuccess(<Trans>Task template was removed</Trans>)
      return response.data?.removeTaskTemplate
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to add remove template</Trans>,
      )
    }
  }

  return (
    <TaskTemplateContext.Provider
      value={{
        loading,
        taskTemplates,
        refetch,
        create,
        update,
        remove,
      }}
    >
      {children}
    </TaskTemplateContext.Provider>
  )
}

function useTaskTemplates() {
  return useContext(TaskTemplateContext)
}

export { TaskTemplateProvider, useTaskTemplates }
